import type { Field } from '../types';
import * as geoApi from '../api/geo';
import { KeyedAsyncCache } from './asyncCache';

const STATUS_TO_BACKEND: Record<Field['status'], geoApi.FieldCultivationStatus> = {
  'Đang canh tác': 'CULTIVATING',
  'Tạm dừng': 'PAUSED',
  'Bỏ trống': 'FALLOW',
};
const STATUS_FROM_BACKEND: Record<geoApi.FieldCultivationStatus, Field['status']> = {
  CULTIVATING: 'Đang canh tác',
  PAUSED: 'Tạm dừng',
  FALLOW: 'Bỏ trống',
};

function toFrontendField(f: geoApi.BackendField): Field {
  return {
    id: f._id,
    farmerId: f.farmerId,
    maThuaDat: f.code || '',
    name: f.name || '',
    area: f.areaHa || 0,
    location: f.location || '',
    loaiDat: f.soilType || '',
    status: STATUS_FROM_BACKEND[f.cultivationStatus] || 'Đang canh tác',
    hinhAnh: f.photoFileId,
    ghiChu: f.notes,
    taiLieu: f.documentFileIds ?? [],
  };
}

const byFarmer = new KeyedAsyncCache<Field[]>(
  async (farmerId) => (await geoApi.listFieldsByFarmer(farmerId)).map(toFrontendField),
  [],
  'fields:byFarmer',
);

export function getFieldsByFarmer(farmerId?: string): Field[] {
  if (!farmerId) return [];
  return byFarmer.get(farmerId);
}

/** False while the farmer's fields are still being fetched — `getFieldsByFarmer`
 * returns [] then too, which must not be read as "this farmer has no field". */
export function areFieldsLoaded(farmerId?: string): boolean {
  return !!farmerId && byFarmer.has(farmerId);
}

/** The field-creation UI (`FieldTab`) only collects business attributes (name, area,
 * soil type) — it has no map-drawing/GPS-walk step to capture real plot geometry.
 * Backend `Field.geometry` is required (drives overlap-detection), so a placeholder
 * polygon stands in until a real boundary-capture UI exists — see task 27 notes. */
function placeholderGeometry(): number[][][] {
  return [[[105, 10], [105.001, 10], [105.001, 10.001], [105, 10.001], [105, 10]]];
}

export async function addField(data: Omit<Field, 'id'>): Promise<Field> {
  const created = await geoApi.createField({
    farmerId: data.farmerId,
    geometry: { coordinates: placeholderGeometry() },
    source: 'HAND_DRAW',
    areaHa: data.area,
    code: data.maThuaDat,
    name: data.name,
    location: data.location,
    soilType: data.loaiDat,
    photoFileId: data.hinhAnh,
    documentFileIds: data.taiLieu,
    notes: data.ghiChu,
  });
  byFarmer.invalidate(data.farmerId);
  byFarmer.get(data.farmerId);
  return toFrontendField(created);
}

export async function updateField(id: string, data: Partial<Field>): Promise<Field> {
  const updated = await geoApi.updateField(id, {
    code: data.maThuaDat,
    name: data.name,
    location: data.location,
    soilType: data.loaiDat,
    cultivationStatus: data.status ? STATUS_TO_BACKEND[data.status] : undefined,
    notes: data.ghiChu,
    photoFileId: data.hinhAnh,
    documentFileIds: data.taiLieu,
  });
  const field = toFrontendField(updated);
  byFarmer.invalidate(field.farmerId);
  byFarmer.get(field.farmerId);
  return field;
}

export async function deleteField(id: string, farmerId: string): Promise<void> {
  await geoApi.deleteField(id);
  byFarmer.invalidate(farmerId);
  byFarmer.get(farmerId);
}
